/** @velyn-custom BEGIN — vely-shop-the-look.js (NPS-style bleed + slidesOffsetBefore/After; shopable-image-slide + VelySwiperPagination) */
(function () {
  const MOBILE_MQ = "(max-width: 767.98px)";

  function getSlideHost(root) {
    return root?.querySelector?.("shopable-image-slide");
  }

  function getLeftInsetPx(root) {
    const section = root.closest(".section-vely-shop-the-look");
    const shell = root.querySelector(".vely-stl__slider-shell");
    if (!section || !shell) return 0;

    const titleBox = section.querySelector(".vely-stl__header-wrap");
    const shellLeft = shell.getBoundingClientRect().left;

    if (titleBox) {
      const pad = parseFloat(getComputedStyle(titleBox).paddingInlineStart) || 0;
      return Math.max(
        0,
        Math.round(titleBox.getBoundingClientRect().left + pad - shellLeft)
      );
    }

    const probe = document.createElement("div");
    probe.setAttribute("aria-hidden", "true");
    probe.style.cssText =
      "position:absolute;visibility:hidden;pointer-events:none;height:0;width:var(--vely-stl-left-inset);";
    section.appendChild(probe);
    const px = Math.round(probe.getBoundingClientRect().width) || 0;
    probe.remove();
    return Math.max(0, px);
  }

  function getSlideMaxWidthPx(root) {
    const section = root.closest(".section-vely-shop-the-look");
    if (!section) return 860;
    const raw = getComputedStyle(section).getPropertyValue("--vely-stl-slide-max-w");
    const px = parseFloat(raw);
    if (!Number.isFinite(px) || px <= 0) return 860;
    return Math.min(860, Math.round(px));
  }

  /** Last pagination/snap must land on max translate (container-right aligned). */
  function pinLastSnapToEnd(swiper) {
    if (!swiper?.snapGrid?.length) return;
    const maxSnap = -swiper.maxTranslate();
    const last = swiper.snapGrid.length - 1;
    if (Number.isFinite(maxSnap) && swiper.snapGrid[last] !== maxSnap) {
      swiper.snapGrid[last] = maxSnap;
    }
  }

  /**
   * Desktop/tablet: slidesPerView auto + fixed CSS width (≤860px).
   * Avoids slidesPerView numeric fighting max-width (stretch / end-gap).
   */
  function applyDesktopAutoSlides(root, swiper) {
    if (!swiper || window.matchMedia(MOBILE_MQ).matches) return false;

    const maxW = getSlideMaxWidthPx(root);
    let changed = false;

    if (swiper.params.slidesPerView !== "auto") {
      swiper.params.slidesPerView = "auto";
      changed = true;
    }

    const bps = swiper.params.breakpoints;
    if (bps && typeof bps === "object") {
      Object.keys(bps).forEach((key) => {
        if (Number(key) < 768) return;
        if (bps[key] && bps[key].slidesPerView !== "auto") {
          bps[key].slidesPerView = "auto";
          changed = true;
        }
      });
    }

    swiper.slides?.forEach((slide) => {
      if (slide.classList.contains("swiper-slide-duplicate")) return;
      const next = `${maxW}px`;
      if (slide.style.width !== next) {
        slide.style.width = next;
        changed = true;
      }
    });

    return changed;
  }

  function applyTrackOffsets(root, swiper) {
    if (!swiper) return;
    /* Mobile: gutters from CSS host padding (NPS pattern).
       Desktop: before + after = container gutters so last slide ends at container right. */
    const isMobile = window.matchMedia(MOBILE_MQ).matches;
    const autoChanged = applyDesktopAutoSlides(root, swiper);
    const inset = isMobile ? 0 : getLeftInsetPx(root);
    const before = inset;
    const after = inset;

    if (
      !autoChanged &&
      swiper.params.slidesOffsetBefore === before &&
      swiper.params.slidesOffsetAfter === after
    ) {
      swiper.updateSize?.();
      swiper.updateSlides?.();
      pinLastSnapToEnd(swiper);
      return;
    }
    swiper.params.slidesOffsetBefore = before;
    swiper.params.slidesOffsetAfter = after;
    swiper.update();
    pinLastSnapToEnd(swiper);
  }

  function syncPagination(root, swiper) {
    if (!swiper) return;
    const pill = root.querySelector(".vely-swiper-pagination-pill");
    window.VelySwiperPagination?.setActiveBullet?.(swiper, pill);
  }

  function enhancePagination(root, swiper) {
    if (!swiper) return;
    const pill = root.querySelector(".vely-swiper-pagination-pill");
    window.VelySwiperPagination?.refresh?.(swiper, pill);
    syncPagination(root, swiper);
    window.VelySwiperPagination?.pinInFlow?.(pill);
  }

  function bindPagination(root, swiper) {
    if (!swiper || swiper._velyStlPaginationBound) return;
    swiper._velyStlPaginationBound = true;

    applyTrackOffsets(root, swiper);
    enhancePagination(root, swiper);

    const onSync = () => syncPagination(root, swiper);
    swiper.on("slideChange", onSync);
    swiper.on("slideChangeTransitionEnd", onSync);
    swiper.on("progress", onSync);
    swiper.on("reachEnd", onSync);
    swiper.on("fromEdge", onSync);
    swiper.on("resize", () => {
      applyTrackOffsets(root, swiper);
      enhancePagination(root, swiper);
    });
  }

  function waitForSwiper(root, attempt = 0) {
    const slideHost = getSlideHost(root);
    const swiper = slideHost?.swiper;
    if (swiper?.initialized || swiper?.slides?.length) {
      bindPagination(root, swiper);
      requestAnimationFrame(() => {
        applyTrackOffsets(root, swiper);
        syncPagination(root, swiper);
      });
      root.classList.add("vely-stl--ready");
      return;
    }
    if (attempt > 100) return;
    setTimeout(() => waitForSwiper(root, attempt + 1), 50);
  }

  class VelyShopTheLook extends HTMLElement {
    connectedCallback() {
      if (this._velyStlInitStarted) return;
      this._velyStlInitStarted = true;
      waitForSwiper(this);
    }

    disconnectedCallback() {
      this._velyStlInitStarted = false;
      this.classList.remove("vely-stl--ready");
    }
  }

  if (!customElements.get("vely-shop-the-look")) {
    customElements.define("vely-shop-the-look", VelyShopTheLook);
  }

  document.addEventListener("shopify:section:load", (event) => {
    const host = event.target?.querySelector?.("vely-shop-the-look");
    if (!host) return;
    host._velyStlInitStarted = false;
    host.connectedCallback();
  });
})();
/** @velyn-custom END — vely-shop-the-look.js */
